<template>
  <div class="exhibition-detail">
    <AppHeader />

    <DetailSkeleton v-if="loading" />

    <template v-else-if="detail">
      <DetailBanner :title="detail.title" :image="detail.bannerImage" />

      <div class="exhibition-detail__body">
        <DetailIntro :detail="detail" />

        <section
          v-for="chapter in detail.chapters"
          :key="chapter.id"
          class="exhibition-detail__chapter"
        >
          <div v-if="chapter.title" class="exhibition-detail__chapter-header">
            <h2 class="exhibition-detail__chapter-title">{{ chapter.title }}</h2> 
            <span class="exhibition-detail__chapter-line" />
          </div>
          <p
            v-if="chapter.configure && chapter.configure.description"
            class="exhibition-detail__chapter-desc"
            v-html="chapter.configure.description"
          />

          <DetailSectionContent
            v-if="hasChapterItems(chapter)"
            :section="chapter"
            hide-header
          />

          <DetailSectionContent
            v-for="section in chapter.children || []"
            :key="section.id"
            :section="section"
          />
        </section>
      </div>
    </template>

    <div v-else class="exhibition-detail__empty">
      <p>未找到该主题展览</p>
      <router-link to="/exhibition" class="exhibition-detail__back">返回主题列表</router-link>
    </div>

    <AppFooter />
  </div>
</template>

<script>
import AppHeader from '@/components/common/AppHeader.vue'
import AppFooter from '@/components/common/AppFooter.vue'
import DetailBanner from './components/DetailBanner.vue'
import DetailIntro from './components/DetailIntro.vue'
import DetailSkeleton from './components/DetailSkeleton.vue'
import DetailSectionContent from './components/DetailSectionContent.vue'
import { fetchExhibitionDetail, mediaUrl } from '@/api/exhibition'
import { recordBrowseHistory, LOG_HISTORY_TYPE } from '@/utils/browseHistory'

export default {
  name: 'ExhibitionDetail',
  components: {
    AppHeader,
    AppFooter,
    DetailBanner,
    DetailIntro,
    DetailSkeleton,
    DetailSectionContent
  },
  data() {
    return {
      detail: null,
      loading: false
    }
  },
  watch: {
    '$route.params.id'() {
      this.loadDetail()
    }
  },
  mounted() {
    document.documentElement.classList.add('hide-scrollbar')
    this.loadDetail()
  },
  beforeDestroy() {
    document.documentElement.classList.remove('hide-scrollbar')
  },
  methods: {
    hasChapterItems(chapter) {
      const items = chapter.configure && chapter.configure.items
      return Array.isArray(items) && items.length > 0
    },
    async loadDetail() {
      const id = this.$route.params.id
      if (!id) {
        this.detail = null
        return
      }
      this.loading = true
      try {
        const data = await fetchExhibitionDetail(id)
        const gallery = (data.images || [])
          .map((item) => mediaUrl(item))
          .filter(Boolean)
        const bannerImage = gallery[0] || mediaUrl(data.coverUrl)
        if (!gallery.length && bannerImage) {
          gallery.push(bannerImage)
        }
        this.detail = {
          ...data,
          gallery,
          bannerImage,
          intro: data.description || '',
          eventType: data.categoryName || '专题',
          eventDate: data.publishedTime || ''
        }
        recordBrowseHistory(LOG_HISTORY_TYPE.EXHIBITION, id)
      } catch (error) {
        console.error(error)
        this.detail = null
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.exhibition-detail {
  min-height: 100vh;
  background:
    url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.035'/%3E%3C/svg%3E"),
    $color-paper;

  &__body {
    max-width: $container-max-width;
    margin: 0 auto;
    padding: $spacing-2xl $content-padding $spacing-4xl;
  }

  &__empty {
    padding: $spacing-4xl $content-padding;
    text-align: center;
    color: $color-text-light;

    p {
      margin-bottom: $spacing-base;
      font-size: rem(16);
    }
  }

  &__back {
    color: $success-color;
    font-size: rem(14);

    &:hover {
      text-decoration: underline;
    }
  }

  &__chapter {
    margin-bottom: $spacing-3xl;
  }

  &__chapter-header {
    display: flex;
    align-items: center;
    gap: $spacing-base;
    margin-bottom: $spacing-sm;
  }

  &__chapter-title {
    font-family: $font-serif;
    font-size: rem(26);
    font-weight: 700;
    color: $color-brown-dark;
    letter-spacing: 3px;
    flex-shrink: 0;
  }

  &__chapter-line {
    flex: 1;
    height: 1px;
    background: $color-border;
  }

  &__chapter-desc {
    margin-bottom: $spacing-xl;
    font-size: rem(14);
    line-height: 1.8;
    color: $color-text-light;
    white-space: pre-wrap;
  }
}
</style>
